import uniqueId from 'lodash-es/uniqueId';
import { OptionsObject, useSnackbar, VariantType } from 'notistack';
import { getBettingSlipSnackbarMessage } from '../helpers';
import { BettingSlipAcceptStatus } from '../@types';
import useLocalization from './useLocalization';
import { useCallback } from 'react';

export const useExtendedSnackbar = () => {
  const snackbarContext = useSnackbar();
  const { t, getLocalizedErrorMessage } = useLocalization();

  const enqueueWithDelay = useCallback(
    (message: string, options?: OptionsObject<VariantType>, delay = 0) => {
      const enqueue = () => snackbarContext.enqueueSnackbar(message, options);

      if (delay > 0) {
        setTimeout(enqueue, delay);
      } else {
        enqueue();
      }
    },
    [snackbarContext]
  );

  const showTicketAuthorizationCountMessage = useCallback(
    (status: BettingSlipAcceptStatus, count: number, delay = 0) => {
      const options: OptionsObject<VariantType> = {
        key: ['rejected', 'pending'].includes(status) ? uniqueId('betting-slip') : undefined,
        variant: status === 'accepted' ? 'success' : 'warning',
      };

      const message = getBettingSlipSnackbarMessage(status, count, t);
      enqueueWithDelay(message, options, delay);
    },
    [enqueueWithDelay, t]
  );

  const showErrorMessage = useCallback(
    (errorCode: string, params?: Record<string, string | number>, options?: OptionsObject<VariantType>) => {
      const message = getLocalizedErrorMessage(errorCode, params);

      enqueueWithDelay(message, { variant: 'error', ...options });
    },
    [enqueueWithDelay, getLocalizedErrorMessage]
  );

  return { ...snackbarContext, enqueueWithDelay, showTicketAuthorizationCountMessage, showErrorMessage };
};
